import { useEffect, useRef, useState } from "react";
import { useStreamSelector } from "../../useStreamSelector";
import { StreamSelectors } from "../../StreamSelectors";

// The fault-class the error boundary stamps. `agent_fixable` and `transient`
// both get the transient "we hit an error" framing; `harness` gets the
// "won't recover, report it" framing. A checkpoint written before a deploy can
// still carry the older `"agentRuntime"`/`"provider"` spellings, so the type
// and `hasTransientFraming` accept both — treating the legacy names as their
// current equivalents.
export type StateErrorKind =
  | "agent_fixable"
  | "transient"
  | "harness"
  | "agentRuntime"
  | "provider";

export interface StateErrorPayload {
  message: string;
  node: string;
  kind: StateErrorKind;
}

/**
 * True for the fault classes that get the short "we hit an error" framing — the
 * agent's own fixable failure or a transient provider blip. Anything else,
 * INCLUDING a missing/unknown `kind`, falls back to the harness framing: we must
 * never render the raw `message` (a stack-trace dump) to the user just because a
 * schema stripped the field. Tolerates legacy checkpoint spellings.
 */
function hasTransientFraming(kind: StateErrorKind | undefined): boolean {
  return (
    kind === "agent_fixable" ||
    kind === "transient" ||
    kind === "agentRuntime" ||
    kind === "provider"
  );
}

// "Looks like our GPU's are having a moment",
// "Our AI must've predicted the wrong next token and just kept going",
// "Seems like our AI needs to touch grass (metaphorically, obviously)",
// "Reasoning model forgot to reason",
// "Currently re-reading the entire internet, please hold",
//
export const AI_JOKES = [
  "Vibe marketing, but the vibes are off",
];

export interface ErrorBannerProps {
  /**
   * Optional override for the transport / SDK-level error message
   * (shown when `state.error` is absent but `s.error` is set). Defaults
   * to "Something went wrong. Please try sending your message again."
   */
  message?: string;
  /**
   * Optional override for the structured state-error message. Receives
   * the agent's reported `{message, node, kind}` payload and returns the
   * string to render. The default phrases by kind:
   *
   *   - `agent_fixable` / `transient`: short, transient framing. The chat
   *     already shows a recovery AIMessage — the banner is the explicit
   *     "yes, something went wrong" indicator next to it.
   *   - `harness`: a server bug, named with the node so we can triage.
   *
   * Use this for product-specific phrasing (e.g. map `node` to a
   * user-facing subsystem name). Return an empty string to suppress the
   * banner for a given error.
   */
  renderStateError?: (err: StateErrorPayload) => string;
  className?: string;
  /**
   * Render dismiss affordance (a close button). Default: true. When
   * dismissed the banner stays hidden until the underlying error
   * identity changes — i.e. the user sees it again automatically on
   * a NEW failure, not on a repeat of the one they just acknowledged.
   */
  dismissable?: boolean;
  /**
   * One-click "send this error to our support team" callback. When
   * provided AND a state error is showing, the banner renders an
   * inline "click here to send the error to our Support Team" link
   * after the error text. The consumer wires this up — the package
   * is framework-agnostic (Inertia / fetch / something else lives
   * on the host side). The banner manages the sending/sent UI state.
   */
  onReportToSupport?: (error: StateErrorPayload) => Promise<void> | void;
}

const DEFAULT_STREAM_MESSAGE =
  "Something went wrong. Please try sending your message again.";

const defaultRenderStateError = ({ kind }: StateErrorPayload) => {
  if (hasTransientFraming(kind)) {
    return "Yikes: Looks like our AI agent encountered an error.";
  }
  // Harness errors are our server-side bugs (zod schema failures,
  // unhandled exceptions). The raw `message` is a stack-trace-style
  // dump that leaks internals and is not actionable to the user, so
  // never render it. Tell them the failure won't recover and route them
  // to support — the report button below carries the full payload for
  // triage.
  return "Something went wrong on our end and we won't be able to recover from this one. Please send it to our support team so we can dig in.";
};

/** Pick a stable joke per error identity. Random per failure, sticky during re-renders. */
function pickJoke(): string {
  return AI_JOKES[Math.floor(Math.random() * AI_JOKES.length)]!;
}

/**
 * ErrorBanner — surfaces the two distinct failure signals a Chat can
 * produce:
 *
 *   1. **Transport error** (`StreamSelectors.error`) — the SDK couldn't
 *      complete the request: connection drop, auth fail, server crash
 *      at the HTTP layer. The user should retry the same message.
 *
 *   2. **State error** (`StreamSelectors.stateError`) — the request
 *      succeeded but the agent's `state.error` field is populated.
 *      Our langgraph error boundary writes here when it classifies a
 *      turn as a "harness" failure (our code threw, not the model).
 *      The user has already seen the partial work + recovery message
 *      in the chat above; the banner gives them the structured "what
 *      failed where" detail.
 *
 * State errors take display precedence — they're more specific. The
 * SDK transport error is the fallback.
 *
 * The banner is dismissable per-error-identity: closing it for one
 * failure doesn't suppress future failures with different messages.
 */
export function ErrorBanner({
  message = DEFAULT_STREAM_MESSAGE,
  renderStateError = defaultRenderStateError,
  className,
  dismissable = true,
  onReportToSupport,
}: ErrorBannerProps) {
  const streamError = useStreamSelector(StreamSelectors.error);
  const stateError = useStreamSelector(StreamSelectors.stateError);

  // Build a stable identity for "is this the same error the user just
  // dismissed?" Includes both kinds so a new stream error after a
  // dismissed state error still re-shows the banner.
  const identity = buildIdentity(streamError, stateError);
  const [dismissedIdentity, setDismissedIdentity] = useState<string | null>(null);
  const prevIdentity = useRef<string | null>(null);

  // Per-error-identity report state so a previously-sent identity stays
  // in "sent" if it re-renders, but a NEW error resets back to idle.
  const [reportState, setReportState] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");
  const reportedIdentity = useRef<string | null>(null);

  // Pick a joke ONCE per error identity. Keeps the blockquote stable
  // across re-renders so it doesn't shuffle every time the parent
  // updates, but rotates whenever a new failure shows up.
  const jokeRef = useRef<{ identity: string | null; joke: string }>({
    identity: null,
    joke: "",
  });
  if (jokeRef.current.identity !== identity) {
    jokeRef.current = { identity, joke: pickJoke() };
  }

  // When the error identity changes (new failure), reset the dismiss
  // flag — the new error should be visible regardless of prior dismissals.
  useEffect(() => {
    if (identity !== prevIdentity.current) {
      prevIdentity.current = identity;
      if (identity !== dismissedIdentity) setDismissedIdentity(null);
      // Same trigger for the report button — a new failure means the
      // previous "Sent" pill no longer applies.
      if (identity !== reportedIdentity.current) {
        setReportState("idle");
      }
    }
  }, [identity, dismissedIdentity]);

  const handleReport = async () => {
    if (!onReportToSupport || !stateError) return;
    setReportState("sending");
    try {
      await onReportToSupport(stateError);
      reportedIdentity.current = identity;
      setReportState("sent");
    } catch {
      setReportState("error");
    }
  };

  // No error at all.
  if (!streamError && !stateError) return null;

  // User already dismissed this exact error.
  if (identity && identity === dismissedIdentity) return null;

  // Credit errors are handled by CreditWarningModal, not the banner.
  // This is a stream-error escape; state errors are unrelated to credits.
  if (streamError && !stateError) {
    const errorStr =
      streamError instanceof Error ? streamError.message : String(streamError);
    if (
      errorStr.includes("CREDITS_EXHAUSTED") ||
      errorStr.includes("Insufficient credits")
    ) {
      return null;
    }
  }

  // State error wins display — it's structured + agent-reported.
  const stateText = stateError ? renderStateError(stateError) : "";
  const body = stateText.length > 0 ? stateText : message;
  // Skip if the renderer returned empty (consumer suppressed this error).
  if (!body) return null;

  // Only show the support-report affordance for state errors (the
  // banner has a structured payload to send) AND when the consumer
  // wired up a callback. Transport errors don't have a node/kind to
  // attach, so the report would be lower-signal.
  const showReport = Boolean(onReportToSupport && stateError);

  // Harness errors won't recover on retry — the agent's harness already
  // classified them as our bug, not a transient one. Drop the "Try
  // sending your message again, or" preamble; the support button is the
  // only useful next step.
  const isHarness = stateError?.kind === "harness";

  // Joke blockquote is part of the DEFAULT renderer's visual identity
  // for AI-attributable failures. Consumers who override
  // `renderStateError` get just their text — no joke — so the banner
  // doesn't suddenly tonal-shift between custom and default copy.
  const usingDefaultRenderer = renderStateError === defaultRenderStateError;
  const showJoke =
    usingDefaultRenderer &&
    Boolean(stateError) &&
    hasTransientFraming(stateError?.kind);

  return (
    <div
      className={`mx-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex flex-col gap-2 ${className ?? ""}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <span className="flex-1">
          {body}
          {showReport ? (
            reportState === "sent" ? (
              // Sent: the secondary CTA is consumed. Drop the "or
              // [button]" branch entirely and just acknowledge the
              // hand-off. The retry path on the user's message is
              // still implicit — they can resend whenever.
              " We've notified our team — they'll follow up shortly."
            ) : (
              <>
                {isHarness ? " " : " Try sending your message again, or "}
                <button
                  type="button"
                  onClick={handleReport}
                  disabled={reportState === "sending"}
                  className="ml-0.5 inline-flex items-center rounded-md border border-red-300 bg-white px-2 py-0.5 text-xs font-medium text-red-700 shadow-xs transition-colors hover:bg-red-100 hover:border-red-400 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {reportState === "sending"
                    ? "Sending…"
                    : reportState === "error"
                      ? "Couldn't send — retry"
                      : "Send to Support Team"}
                </button>
              </>
            )
          ) : null}
        </span>
        {dismissable ? (
          <button
            type="button"
            onClick={() => setDismissedIdentity(identity)}
            aria-label="Dismiss error"
            className="text-red-700/70 hover:text-red-900 transition-colors -mr-1"
          >
            ×
          </button>
        ) : null}
      </div>
      {showJoke ? (
        <blockquote className="border-l-2 border-red-300 pl-3 italic text-red-700/70 text-xs">
          {jokeRef.current.joke}
        </blockquote>
      ) : null}
    </div>
  );
}

/**
 * Build a stable string id for an error pair. Identity changes when the
 * underlying error TEXT changes — that's the cue to re-show the banner
 * after a previous dismiss. Two failures with identical text count as
 * the same error (don't re-spam the user).
 */
function buildIdentity(
  streamError: unknown,
  stateError: StateErrorPayload | undefined
): string | null {
  if (stateError) {
    return `state:${stateError.kind}:${stateError.node}:${stateError.message}`;
  }
  if (streamError) {
    const msg =
      streamError instanceof Error ? streamError.message : String(streamError);
    return `stream:${msg}`;
  }
  return null;
}
